import React, { FC, ReactNode, useState } from "react";
import { useDrop, XYCoord }               from "react-dnd";
import { useDispatch, useStore }          from "react-redux";
import RecordTemplateRepository           from "../../../../domain/record/template/RecordTemplateRepository";
import WidgetItems                        from "../../../../domain/record/widget/item/WidgetItems";
import Widget                             from "../../../../domain/record/widget/Widget";
import WidgetCoordinate                   from "../../../../domain/record/widget/WidgetCoordinate";
import WidgetId                           from "../../../../domain/record/widget/WidgetId";
import Widgets                            from "../../../../domain/record/widget/Widgets";
import WidgetTitle                        from "../../../../domain/record/widget/WidgetTitle";
import WidgetType                         from "../../../../domain/record/widget/WidgetType";
import WidgetTypeClass                    from "../../../../domain/record/widget/WidgetTypeClass";
import WidgetValue                        from "../../../../domain/record/widget/WidgetValue";
import RecordTemplateDataSource
                                          from "../../../../infrastructure/datasource/record/template/RecordTemplateDataSource";
import DropAreaCheckHorizontalWidget      from "../widget/drop/DropAreaCheckHorizontalWidget";
import DropAreaCheckVerticalWidget        from "../widget/drop/DropAreaCheckVerticalWidget";
import DropAreaImageA4Widget              from "../widget/drop/DropAreaImageA4Widget";
import DropAreaImageLargeWidget           from "../widget/drop/DropAreaImageLargeWidget";
import DropAreaImageMediumWidget          from "../widget/drop/DropAreaImageMediumWidget";
import DropAreaImageSmallWidget           from "../widget/drop/DropAreaImageSmallWidget";
import DropAreaRadioHorizontalWidget      from "../widget/drop/DropAreaRadioHorizontalWidget";
import DropAreaRadioVerticalWidget        from "../widget/drop/DropAreaRadioVerticalWidget";
import DropAreaTextAreaLargeWidget        from "../widget/drop/DropAreaTextAreaLargeWidget";
import DropAreaTextAreaMediumWidget       from "../widget/drop/DropAreaTextAreaMediumWidget";
import DropAreaTextAreaSmallWidget        from "../widget/drop/DropAreaTextAreaSmallWidget";
import DropAreaTextBoxWidget              from "../widget/drop/DropAreaTextBoxWidget";
import WidgetDragDropProps                from "../widget/WidgetDragDropProps";
import "./css/RecordTemplateDropArea.scss";

const RecordTemplateDropArea: FC = () => {
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);
  const widgets: Widgets = useStore().getState().template.widgets;
  const repository: RecordTemplateRepository = new RecordTemplateDataSource(useDispatch());
  const [, drop] = useDrop({
    accept: [
      WidgetType.TEXT_BOX,
      WidgetType.TEXT_AREA_SMALL,
      WidgetType.TEXT_AREA_MEDIUM,
      WidgetType.TEXT_AREA_LARGE,
      WidgetType.RADIO_VERTICAL,
      WidgetType.RADIO_HORIZONTAL,
      WidgetType.CHECK_VERTICAL,
      WidgetType.CHECK_HORIZONTAL,
      WidgetType.IMAGE_SMALL,
      WidgetType.IMAGE_MEDIUM,
      WidgetType.IMAGE_LARGE,
      WidgetType.IMAGE_A4,
    ],
    drop: (item: WidgetDragDropProps, monitor) => {
      if (widgets.has(item.widgetIndex)) {
        const widget: Widget = widgets.findByIndex(item.widgetIndex);
        const coordinate: XYCoord | null = monitor.getDifferenceFromInitialOffset();
        if (coordinate !== null) {
          const moveX = coordinate.x;
          const moveY = coordinate.y;
          repository.moveWidget(item.widgetIndex, new Widget(widget.id, widget.type, widget.coordinate.move(moveX, moveY), widget.title, widget.value, widget.items));
        } else {
          repository.moveWidget(item.widgetIndex, new Widget(widget.id, widget.type, new WidgetCoordinate(x, y), widget.title, widget.value, widget.items));
        }
      } else {
        repository.addWidget(new Widget(
          new WidgetId(),
          WidgetTypeClass.of(item.type.toString()),
          new WidgetCoordinate(x, y),
          new WidgetTitle(),
          new WidgetValue(),
          new WidgetItems()
        ));
      }
    },
    collect: monitor => ({
      canDrop: monitor.canDrop(),
      isOver: monitor.isOver(),
    })
  });

  const onMouseMove = (event) => {
    const dropArea: HTMLElement | null = document.getElementById("dropArea");
    if (dropArea !== null) {
      const rect: ClientRect | DOMRect = dropArea.getBoundingClientRect();
      setX(event.clientX - rect.left);
      setY(event.clientY - rect.top);
    }
  };

  const viewWidget = (widgetIndex: number, widget: Widget): ReactNode => {
    switch (widget.type.value) {
      default:
      case WidgetType.NONE:
        return null;
      case WidgetType.TEXT_BOX:
        return (<DropAreaTextBoxWidget widgetIndex={ widgetIndex } widget={ widget } key={ widgetIndex }/>);
      case WidgetType.TEXT_AREA_SMALL:
        return (<DropAreaTextAreaSmallWidget widgetIndex={ widgetIndex } widget={ widget } key={ widgetIndex }/>);
      case WidgetType.TEXT_AREA_MEDIUM:
        return (<DropAreaTextAreaMediumWidget widgetIndex={ widgetIndex } widget={ widget } key={ widgetIndex }/>);
      case WidgetType.TEXT_AREA_LARGE:
        return (<DropAreaTextAreaLargeWidget widgetIndex={ widgetIndex } widget={ widget } key={ widgetIndex }/>);
      case WidgetType.RADIO_VERTICAL:
        return (<DropAreaRadioVerticalWidget widgetIndex={ widgetIndex } widget={ widget } key={ widgetIndex }/>);
      case WidgetType.RADIO_HORIZONTAL:
        return (<DropAreaRadioHorizontalWidget widgetIndex={ widgetIndex } widget={ widget } key={ widgetIndex }/>);
      case WidgetType.CHECK_VERTICAL:
        return (<DropAreaCheckVerticalWidget widgetIndex={ widgetIndex } widget={ widget } key={ widgetIndex }/>);
      case WidgetType.CHECK_HORIZONTAL:
        return (<DropAreaCheckHorizontalWidget widgetIndex={ widgetIndex } widget={ widget } key={ widgetIndex }/>);
      case WidgetType.IMAGE_SMALL:
        return (<DropAreaImageSmallWidget widgetIndex={ widgetIndex } widget={ widget } key={ widgetIndex }/>);
      case WidgetType.IMAGE_MEDIUM:
        return (<DropAreaImageMediumWidget widgetIndex={ widgetIndex } widget={ widget } key={ widgetIndex }/>);
      case WidgetType.IMAGE_LARGE:
        return (<DropAreaImageLargeWidget widgetIndex={ widgetIndex } widget={ widget } key={ widgetIndex }/>);
      case WidgetType.IMAGE_A4:
        return (<DropAreaImageA4Widget widgetIndex={ widgetIndex } widget={ widget } key={ widgetIndex }/>);
    }
  };

  return (
    <>
      <div
        ref={ drop }
        className="dropArea"
        id="dropArea"
        onMouseMove={ onMouseMove }
      >
        {
          widgets.toList().map((widget, index) => (
            <>
              { viewWidget(index, widget) }
            </>
          ))
        }
      </div>
    </>
  );
};

export default RecordTemplateDropArea;
